import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetTrigger, SheetContent, SheetClose } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { 
  Menu, 
  History, 
  LogOut, 
  CreditCard, 
  HelpCircle, 
  Sparkles, 
  Gift, 
  Search,
  User,
  FileText,
  Shield
} from "lucide-react";
import { Link } from "react-router-dom";
import { User as SupabaseUser } from "@supabase/supabase-js";

interface MobileSidebarProps {
  user: SupabaseUser | null;
  isSubscribed: boolean;
  isAdmin: boolean;
  showDiscountBadge: boolean;
  discountCode: string | null;
  discountHoursLeft: number | null;
  signOut: () => void;
  onShowSubscription?: () => void;
  onShowChangelog?: () => void;
}

export function MobileSidebar({
  user,
  isSubscribed,
  isAdmin,
  showDiscountBadge,
  discountCode,
  discountHoursLeft,
  signOut,
  onShowSubscription,
  onShowChangelog,
}: MobileSidebarProps) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Abrir menú">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] bg-background border-border p-0 flex flex-col">
        {/* Header with Branding */}
        <div className="p-6 border-b border-border">
          <Link to="/" className="flex items-center gap-3">
            <img src="/inteligencialegal-logo-square.png" alt="DoctIA Logo" className="h-8 w-8" />
            <span className="text-xl font-semibold text-foreground">DoctIA</span>
          </Link>
        </div>

        {/* User Info Section */}
        {user && (
          <div className="px-6 py-4 bg-secondary/30">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                {user.user_metadata?.name && (
                  <p className="font-medium text-sm text-foreground truncate">
                    {user.user_metadata.name}
                  </p>
                )}
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
              </div>
            </div>
            {isSubscribed && (
              <Badge variant="secondary" className="mt-3 text-xs">
                <Sparkles className="h-3 w-3 mr-1" />
                Suscriptor activo
              </Badge>
            )}
          </div>
        )}

        {/* Main Navigation */}
        <div className="flex-1 overflow-y-auto">
          {user ? (
            <>
              {/* Discount Offer Banner */}
              {!isSubscribed && showDiscountBadge && (
                <div className="px-4 py-3">
                  <SheetClose asChild>
                    <Link to={`/planes?code=${discountCode}`}>
                      <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                        <div className="flex items-center gap-2 text-primary">
                          <Gift className="h-4 w-4" />
                          <span className="font-medium text-sm">40% OFF disponible</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {discountHoursLeft}h restantes para aprovechar
                        </p>
                      </div>
                    </Link>
                  </SheetClose>
                </div>
              )}

              {/* Subscribe CTA for non-subscribers */}
              {!isSubscribed && (
                <div className="px-4 py-3 border-b border-border">
                  <SheetClose asChild>
                    <Link to="/planes">
                      <Button className="w-full" size="sm">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Suscribite ahora
                      </Button>
                    </Link>
                  </SheetClose>
                </div>
              )}

              {/* Navigation Section */}
              <div className="py-2">
                <p className="px-6 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Navegación
                </p>
                <nav className="space-y-2 px-3">
                  <SheetClose asChild>
                    <Link to="/buscar">
                      <Button variant="ghost" className="w-full justify-start h-10">
                        <Search className="h-4 w-4 mr-3" />
                        Buscar
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/historial">
                      <Button variant="ghost" className="w-full justify-start h-10">
                        <History className="h-4 w-4 mr-3" />
                        Historial
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/ayuda">
                      <Button variant="ghost" className="w-full justify-start h-10">
                        <HelpCircle className="h-4 w-4 mr-3" />
                        Ayuda
                      </Button>
                    </Link>
                  </SheetClose>
                </nav>
              </div>

              <Separator />

              {/* Account Section */}
              <div className="py-2">
                <p className="px-6 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Cuenta
                </p>
                <nav className="space-y-2 px-3">
                  {onShowChangelog && (
                    <SheetClose asChild>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start h-10"
                        onClick={onShowChangelog}
                      >
                        <Sparkles className="h-4 w-4 mr-3" />
                        Ver novedades
                      </Button>
                    </SheetClose>
                  )}
                  {isAdmin && (
                    <SheetClose asChild>
                      <Link to="/admin">
                        <Button variant="ghost" className="w-full justify-start h-10">
                          <Shield className="h-4 w-4 mr-3" />
                          Administración
                        </Button>
                      </Link>
                    </SheetClose>
                  )}
                  {onShowSubscription ? (
                    <SheetClose asChild>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start h-10"
                        onClick={onShowSubscription}
                      >
                        <CreditCard className="h-4 w-4 mr-3" />
                        Gestionar suscripción
                      </Button>
                    </SheetClose>
                  ) : (
                    <SheetClose asChild>
                      <Link to="/suscripcion">
                        <Button variant="ghost" className="w-full justify-start h-10">
                          <CreditCard className="h-4 w-4 mr-3" />
                          Gestionar suscripción
                        </Button>
                      </Link>
                    </SheetClose>
                  )}
                </nav>
              </div>
            </>
          ) : (
            /* Guest Navigation */
            <div className="py-6 px-4">
              <div className="flex flex-col gap-4">
                <SheetClose asChild>
                  <Link to="/planes">
                    <Button className="w-full" size="lg">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Ver planes
                    </Button>
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/auth">
                    <Button variant="outline" className="w-full" size="lg">
                      Iniciar sesión
                    </Button>
                  </Link>
                </SheetClose>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-auto border-t border-border">
          {user && (
            <div className="p-3">
              <SheetClose asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start h-10 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={signOut}
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  Cerrar sesión
                </Button>
              </SheetClose>
            </div>
          )}
          
          {/* Legal Links */}
          <div className="px-6 py-4 bg-secondary/20">
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <Link to="/terminos" className="hover:text-foreground transition-colors">
                Términos
              </Link>
              <span>·</span>
              <Link to="/privacidad" className="hover:text-foreground transition-colors">
                Privacidad
              </Link>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2">
              © {new Date().getFullYear()} DoctIA
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
